import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { useDrop } from 'react-dnd'
import { ControlledMenu, MenuItem } from '@szhsin/react-menu'
import '@szhsin/react-menu/dist/index.css'

import { BaseEventModel } from '@/app/models/baseEventModel'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TmsDataModel } from '@/app/models/tmsDataModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { TripVehicleType } from '@/app/constants/delegation/tripVehicleType'
import { FaIcon, faArrowDownToLine, faArrowUpFromLine } from '@/lib/fa'
import { getCountryFlag } from '@/app/services/country/countryFlagResolver'
import { eventKey } from '@/app/common/newDelegation/eventIdentity'

import Timeline from '@/modules/drivers/tabs/activities/components_new/Timeline'
import { TimelineProps } from '@/modules/drivers/tabs/activities/components_new/timelineProps'
import {
  createTimelineScene,
  TimelineScene,
} from '@/modules/drivers/tabs/activities/components_new/scene/TimelineScene'
import { SCENE_HEIGHT } from '@/modules/drivers/tabs/activities/components_new/utils/layout'
import {
  assignPointLanes,
  MARKER_BOX_WIDTH,
  MAX_MARKER_LANES,
} from '@/modules/drivers/tabs/activities/components_new/layers/pointLanes'

import DelegationMarkerEditBlock from './DelegationMarkerEditBlock'
import {
  MARKER_DROP_SLACK,
  MarkerDropArea,
  toMovedEventPayload,
} from './markerDrag'
import { BusinessTripTimelineProps } from '../types'

const HEIGHT = SCENE_HEIGHT

const BORDER = 2


const DROP_EXTEND_TOP = MARKER_DROP_SLACK
const DROP_EXTEND_BOTTOM = MARKER_DROP_SLACK


const DROP_HIGHLIGHT_BG = 'rgba(59, 130, 246, 0.12)'

const NEW_ROW: EventModel = {
  tripEvent: TripEventType.NotSet,
  isNew: true,
  temporary: true,
  vehicleType: TripVehicleType.NotSet,
  city: '',
  comments: '',
  date: undefined,
  isTransit: false,
  registrationNumber: '',
  isChecked: false,
  lp: -1,
  country: { code: 'pl', name: 'Polska', nationId: 40, id: '1' },
}

const SHORTCUTS: Partial<Record<TripEventType, string>> = {
  [TripEventType.Begin]: 'Shift + LPM',
  [TripEventType.Finish]: 'Ctrl + LPM',
  [TripEventType.ChangeCountry]: 'Alt + LPM',
  [TripEventType.Loading]: 'Ctrl + Z + LPM',
  [TripEventType.Unloading]: 'Ctrl + X + LPM',
}

const MenuLabel = ({
  label,
  shortcut,
}: {
  label: string
  shortcut?: string
}) => {
  if (!shortcut) return <>{label}</>
  return (
    <span
      style={{ display: 'flex', justifyContent: 'space-between', gap: '24px' }}
    >
      <span>{label}</span>
      <span style={{ color: '#aaa', fontSize: '11px', alignSelf: 'center' }}>
        {shortcut}
      </span>
    </span>
  )
}

const positionToDate = (
  startTime: Moment,
  width: number,
  days: number,
  x: number,
): Moment => {
  const s = moment(startTime)
  const secondsInPeriod = s.clone().add(days, 'days').diff(s, 'seconds')
  const clampedX = Math.max(0, Math.min(width, x))
  const seconds = width > 0 ? (clampedX * secondsInPeriod) / width : 0
  return s.clone().add(seconds, 'seconds')
}

const isEndingEvent = (tripEvent: TripEventType): boolean =>
  tripEvent === TripEventType.Finish ||
  tripEvent === TripEventType.TripExclusionEnd

const removeDuplicates = (data: Array<any> | undefined): Array<any> => {
  if (!data || data.length === 0) return []
  const uniqueMap = new Map<string, any>()
  data.forEach((item) => {
    const key = `${item.startTime}_${item.endTime}_${item.type || ''}_${item.entryType || ''}`
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, item)
    } else {
      const existing = uniqueMap.get(key)
      if (item.id && existing.id && item.id < existing.id) {
        uniqueMap.set(key, item)
      }
    }
  })
  return Array.from(uniqueMap.values())
}

const delegationMarkerTarget = {
  canDrop() {
    return true
  },

  hover(props: BusinessTripTimelineProps & any, monitor: any, component: any) {
    const clientOffset = monitor.getClientOffset()
    if (!clientOffset) return

  
    const area: MarkerDropArea | null = component?.measureMarkerArea?.() ?? null
    const originX = area
      ? area.left
      : (component?.state?.leftPosition || 0) + BORDER
    const x = clientOffset.x - originX
    const date = positionToDate(props.startTime, props.width, 1, x)

    const item = monitor.getItem()
    item.time = moment(date).format('HH:mm:ss')
    item.rawDate = moment(date)
    item.dropArea = area
  },

  drop(props: BusinessTripTimelineProps & any, monitor: any) {
    if (monitor.didDrop()) return null
    const item = monitor.getItem()
    if (!item.rawDate) return null
    const date = item.rawDate.clone().add(-1, 'seconds')

    if (item.position != null) {
      props.editEvent(toMovedEventPayload(item, date), true)
    } else {
      props.addEvent({
        ...NEW_ROW,
        tripEvent: item.tripEvent,
        date,
        temporary: true,
      })
    }
    return { moved: true }
  },
}

type BusinessTripTimelineNewState = {
  addStartTime: Moment | null
  leftPosition: number
  menuAnchor: { x: number; y: number } | null
}

class BusinessTripTimelineNew extends React.Component<
  BusinessTripTimelineProps & any,
  BusinessTripTimelineNewState
> {
  private pressedKeys = new Set<string>()

  constructor(props: BusinessTripTimelineProps & any) {
    super(props)
    this.state = { addStartTime: null, leftPosition: 0, menuAnchor: null }
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown)
    document.addEventListener('keyup', this.handleKeyUp)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
    document.removeEventListener('keyup', this.handleKeyUp)
  }

  handleKeyDown = (e: KeyboardEvent) => {
    this.pressedKeys.add(e.key.toLowerCase())
  }

  handleKeyUp = (e: KeyboardEvent) => {
    this.pressedKeys.delete(e.key.toLowerCase())
  }

  private dayEnd = () => this.props.startTime.clone().add(1, 'days')

  private filterData = (
    inputData: ReadonlyArray<any> | undefined,
    customEndTime?: string,
  ): Array<any> => {
    const startTime = this.props.startTime
    const endTime = this.dayEnd()
    const uniqueData = removeDuplicates(inputData as Array<any>)
    const result: Array<any> = []
    for (const item of uniqueData) {
      let currentEndTime = item.endTime
        ? moment(item.endTime)
        : moment(this.props.currentDate).endOf('day')
      if (customEndTime != null) {
        currentEndTime = moment(item[customEndTime])
      }
      if (currentEndTime < startTime || moment(item.startTime) > endTime) {
        continue
      }
      result.push(item)
    }
    return result
  }

  private filterEvents = (
    inputData: Array<EventModel> | undefined,
  ): Array<EventModel> => {
    const startTime = this.props.startTime
    const endTime = this.dayEnd()
    const result: Array<EventModel> = []
    if (inputData == null) return result
    for (const item of inputData) {
      const eventTime = moment(item.date)
      if (eventTime.isBefore(startTime) || eventTime.isAfter(endTime)) continue
      if (eventTime.isSame(startTime) && isEndingEvent(item.tripEvent)) continue
      if (eventTime.isSame(endTime) && !isEndingEvent(item.tripEvent)) continue
      result.push(item)
    }
    return result
  }

  // Shift+mousedown rozszerza zaznaczenie tekstu na stronie - na clicku jest juz za pozno.
  handleChartMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!e.shiftKey) return
    if ((e.target as HTMLElement).closest('[draggable="true"]')) return
    e.preventDefault()
  }

  handleChartClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!e.shiftKey && !e.ctrlKey && !e.altKey) return

    let eventType: TripEventType | null = null
    if (e.shiftKey && !e.ctrlKey && !e.altKey) {
      eventType = TripEventType.Begin
    } else if (
      e.ctrlKey &&
      !e.shiftKey &&
      !e.altKey &&
      this.pressedKeys.has('z')
    ) {
      eventType = TripEventType.Loading
    } else if (
      e.ctrlKey &&
      !e.shiftKey &&
      !e.altKey &&
      this.pressedKeys.has('x')
    ) {
      eventType = TripEventType.Unloading
    } else if (e.ctrlKey && !e.shiftKey && !e.altKey) {
      eventType = TripEventType.Finish
    } else if (e.altKey && !e.ctrlKey && !e.shiftKey) {
      eventType = TripEventType.ChangeCountry
    }

    if (eventType === null) return
    e.preventDefault()

    const positionX = e.clientX - this.state.leftPosition - BORDER
    const date = positionToDate(
      this.props.startTime,
      this.props.width,
      1,
      positionX,
    )
    this.props.addEvent({
      ...NEW_ROW,
      tripEvent: eventType,
      date,
      temporary: true,
    })
  }

  handleContextMenuClick = (_e: any, eventType: TripEventType) => {
    if (!this.state.addStartTime) return
    this.props.addEvent({
      ...NEW_ROW,
      tripEvent: eventType,
      date: this.state.addStartTime,
      temporary: true,
    })
  }

  private measureLeft = (node: HTMLDivElement | null) => {
    if (node != null) {
      const rect = node.getBoundingClientRect()
      if (this.state.leftPosition !== rect.left) {
        this.setState({ leftPosition: rect.left })
      }
    }
  }

  private markerLaneNode: HTMLDivElement | null = null


  measureMarkerArea = (): MarkerDropArea | null => {
    if (this.markerLaneNode == null) return null
    const rect = this.markerLaneNode.getBoundingClientRect()
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    }
  }

  private renderGpsPoint = (
    scene: TimelineScene,
    point: any,
    index: number,
  ) => {
    const x = scene.timeToX(point.date)
    if (x < 0 || x > scene.contentWidth) return null
    const color = point.source === 'gps' ? '#3b82f6' : '#7c3aed'
    const icon = point.isArrival ? faArrowDownToLine : faArrowUpFromLine
    const ICON_SIZE = 20
    const NEEDLE_HEIGHT = 40
    const tooltipData = JSON.stringify({
      date: moment(point.date).format('DD.MM.YYYY HH:mm'),
      countryName: point.country?.name,
      isArrival: point.isArrival,
      source: point.source,
      odometerValue: point.odometerValue,
    })
    return (
      <div
        key={`gps-${index}`}
        style={{
          position: 'absolute',
          left: x - ICON_SIZE / 2,
          top: 4,
          zIndex: 110,
          pointerEvents: 'auto',
        }}
        data-tip={tooltipData}
        data-for="gpsDataTooltip"
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: ICON_SIZE, color, lineHeight: 0 }}>
            <FaIcon icon={icon} />
          </div>
          <div style={{ width: 2, height: NEEDLE_HEIGHT, background: color }} />
        </div>
        {point.country?.code && (
          <img
            style={{ position: 'absolute', top: 3, right: -20 }}
            src={getCountryFlag(
              point.country.code.toUpperCase() === 'GB'
                ? 'EN'
                : point.country.code,
            )}
            width={18}
            height={18}
          />
        )}
      </div>
    )
  }

  render() {
    const { canDrop, isOver } = this.props
    const isDropHovered = !!isOver && !!canDrop
    const scene = createTimelineScene({
      startTime: this.props.startTime,
      days: 1,
      width: this.props.width,
      height: HEIGHT,
    })

    const currentMarkers = this.filterEvents(
      this.props.delegationMarkers || [],
    ).sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
    const otherTripEvents = this.filterEvents(this.props.tripEvents || [])
    const gpsPoints = this.props.gpsPoints || []
    const markerXs = currentMarkers.map((m) => scene.timeToX(m.date))
    const markerLanes = assignPointLanes(markerXs, {
      boxWidth: MARKER_BOX_WIDTH,
      maxLanes: MAX_MARKER_LANES,
    })

    const timelineProps = {
      startTime: this.props.startTime,
      days: 1,
      width: this.props.width,
      height: HEIGHT,
      extraCurrentDate: this.props.currentDate,
      translate: this.props.translate,
      markInactiveVehicles: true,

      activities: this.filterData(this.props.activities),
      places: this.filterData(this.props.places),
      cardInserts: this.filterData(this.props.cardInserts),
      dayActivities: this.filterData(this.props.dayActivities),
      violations: this.filterData(this.props.violations),
      dailyRests: this.filterData(this.props.dailyRests, 'formalRestEndDate'),
      dailyPartialRegularRests: this.filterData(
        this.props.dailyPartialRegularRests,
        'formalRestEndDate',
      ),
      weeklyRests: this.filterData(this.props.weeklyRests),
      weeklyReducedRests: this.filterData(this.props.weeklyReducedRests),
      breaks: this.filterData(this.props.breaks, 'formalRestEndDate'),
      partialBreaks: this.filterData(
        this.props.partialBreaks,
        'formalRestEndDate',
      ),
      weekRecompenses: this.filterData(
        this.props.weekRecompenses,
        'realRestEndTime',
      ),
      outOfScopes: this.filterData(this.props.outOfScopes),
      existingDelegations: this.filterData(this.props.existingDelegations),
      tmsData: (this.props.tmsData || []) as Array<TmsDataModel>,
      minimalPaymentCountries: this.filterData(
        this.props.minimalPaymentCountries,
      ),
      changeAreas: [] as Array<BaseEventModel>,

      allTripEvents: otherTripEvents,
      delegationMarkers: [],
      allPreviousTripEvents: [],
      allEvents: this.props.allEvents || [],
      countries: this.props.countries,
      periodSelections: [],

      selectedActivity: null,
      editedActivity: null,
      isAddingNew: false,

      simpleMode: false,
      editMode: false,
      flattenChart: false,
      disableTooltips: false,
      readOnly: this.props.readonly,
      isRestsOnBaseModeOn: this.props.isRestsOnBaseModeOn,
      minimalPaymentSuppressedPerSpan: true,
      stackOverlappingMarkers: true,
      dimOtherTripMarkers: true,
      canEdit: false,
      canGenerateAttestation: false,
      showRests: true,
      showViolations: true,
      showTrips: true,
      showDelegations: true,
      showRestParts: true,
      showDayActivities: false,

      onSelected: () => {},
      onAdd: () => {},
      onEdit: () => {},
      onContextMenuChanged: () => {},
      onViolationSelected: () => {},
      onPeriodSelectionsChanged: () => {},
      onRestClick: this.props.onRestClick,
    } as unknown as TimelineProps

    return (
      <div
        ref={(node) => {
          this.measureLeft(node)
          this.props.dropRef(node)
        }}
        style={{ background: 'transparent', position: 'relative' }}
        onMouseDown={this.handleChartMouseDown}
        onClick={this.handleChartClick}
      >
        <span
          onContextMenu={(event) => {
            event.preventDefault()
            event.stopPropagation()
            // react-contextmenu dawał w detail.position.x dokładnie clientX
            const positionX = event.clientX - this.state.leftPosition - BORDER
            const date = positionToDate(
              this.props.startTime,
              this.props.width,
              1,
              positionX,
            )
            this.setState({
              addStartTime: date,
              menuAnchor: { x: event.clientX, y: event.clientY },
            })
          }}
        >
          <div style={{ position: 'relative', width: this.props.width }}>
            <Timeline {...timelineProps} />

            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: this.props.width,
                height: scene.height,
                borderStyle: 'dashed',
                borderWidth: BORDER,
                borderColor: 'transparent',
                background: isDropHovered ? DROP_HIGHLIGHT_BG : undefined,
                pointerEvents: 'none',
                zIndex: 130,
              }}
            >
              <div
                ref={(node) => {
                  this.markerLaneNode = node
                }}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '100%',

                  paddingBottom: scene.bottomPadding,
                }}
              >
                {currentMarkers.map((marker, index) => (
                  <DelegationMarkerEditBlock
                    key={eventKey(marker)}
                    delegationMarker={marker}
                    xPosition={markerXs[index]}
                    lane={markerLanes[index]}
                    bottomPadding={scene.bottomPadding}
                    events={this.props.allEvents || []}
                    allPreviousTripEvents={[]}
                    translate={this.props.translate}
                    countries={this.props.countries}
                    readOnly={!!this.props.readonly}
                    current={true}
                    editEvent={this.props.editEvent}
                    addEvent={this.props.addEvent}
                    removeEvents={this.props.removeEvents}
                    hideDelegationMarkerPopups={
                      this.props.hideDelegationMarkerPopups
                    }
                    markNewEvent={this.props.markNewEvent}
                  />
                ))}

                {gpsPoints.map((point: any, index: number) =>
                  this.renderGpsPoint(scene, point, index),
                )}
              </div>
            </div>

            {canDrop && (
              <>
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: -DROP_EXTEND_TOP,
                    width: '100%',
                    height: DROP_EXTEND_TOP,
                    pointerEvents: 'auto',
                    zIndex: 50,
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    bottom: -DROP_EXTEND_BOTTOM,
                    width: '100%',
                    height: DROP_EXTEND_BOTTOM,
                    pointerEvents: 'auto',
                    zIndex: 50,
                  }}
                />
              </>
            )}
          </div>

          <ControlledMenu
            anchorPoint={this.state.menuAnchor ?? undefined}
            state={this.state.menuAnchor ? 'open' : 'closed'}
            onClose={() => this.setState({ menuAnchor: null })}
            onContextMenu={(e) => e.preventDefault()}
            className="tw-z-[10000]"
            portal
          >
            <MenuItem
              onClick={(e) =>
                this.handleContextMenuClick(e, TripEventType.Begin)
              }
            >
              <MenuLabel
                label={this.props.translate('tripEventType.Begin')}
                shortcut={SHORTCUTS[TripEventType.Begin]}
              />
            </MenuItem>
            <MenuItem
              onClick={(e) =>
                this.handleContextMenuClick(e, TripEventType.Finish)
              }
            >
              <MenuLabel
                label={this.props.translate('tripEventType.Finish')}
                shortcut={SHORTCUTS[TripEventType.Finish]}
              />
            </MenuItem>
            <MenuItem
              onClick={(e) =>
                this.handleContextMenuClick(e, TripEventType.ChangeCountry)
              }
            >
              <MenuLabel
                label={this.props.translate('tripEventType.ChangeCountry')}
                shortcut={SHORTCUTS[TripEventType.ChangeCountry]}
              />
            </MenuItem>
            <MenuItem
              onClick={(e) =>
                this.handleContextMenuClick(e, TripEventType.TripExclusionStart)
              }
            >
              <MenuLabel
                label={this.props.translate('tripEventType.TripExclusionStart')}
              />
            </MenuItem>
            <MenuItem
              onClick={(e) =>
                this.handleContextMenuClick(e, TripEventType.TripExclusionEnd)
              }
            >
              <MenuLabel
                label={this.props.translate('tripEventType.TripExclusionEnd')}
              />
            </MenuItem>
            <MenuItem
              onClick={(e) =>
                this.handleContextMenuClick(e, TripEventType.Arrive)
              }
            >
              <MenuLabel label={this.props.translate('tripEventType.Arrive')} />
            </MenuItem>
            <MenuItem
              onClick={(e) =>
                this.handleContextMenuClick(e, TripEventType.Leave)
              }
            >
              <MenuLabel label={this.props.translate('tripEventType.Leave')} />
            </MenuItem>
            <MenuItem
              onClick={(e) =>
                this.handleContextMenuClick(e, TripEventType.Loading)
              }
            >
              <MenuLabel
                label={this.props.translate('tripEventType.Loading')}
                shortcut={SHORTCUTS[TripEventType.Loading]}
              />
            </MenuItem>
            <MenuItem
              onClick={(e) =>
                this.handleContextMenuClick(e, TripEventType.Unloading)
              }
            >
              <MenuLabel
                label={this.props.translate('tripEventType.Unloading')}
                shortcut={SHORTCUTS[TripEventType.Unloading]}
              />
            </MenuItem>
          </ControlledMenu>
        </span>
      </div>
    )
  }
}


const BusinessTripTimelineNewDnd = (props: BusinessTripTimelineProps & any) => {
  const instanceRef = React.useRef<any>(null)
  const [{ isOver, canDrop }, dropRef] = useDrop({
    accept: 'DELEGATION_MARKER',
    canDrop: () => delegationMarkerTarget.canDrop(),
    hover: (_item, monitor) =>
      delegationMarkerTarget.hover(props, monitor, instanceRef.current),
    drop: (_item, monitor) => delegationMarkerTarget.drop(props, monitor),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })

  return (
    <BusinessTripTimelineNew
      {...props}
      ref={instanceRef}
      isOver={isOver}
      canDrop={canDrop}
      dropRef={dropRef}
    />
  )
}

export default BusinessTripTimelineNewDnd
